'use client'

import React, { useMemo } from 'react'
import { Box, SimpleGrid, Text } from '@mantine/core'
import { staggerContainer, staggerItem } from '@/lib/animations'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import type { SkuComponentConfigMap, SkuVideoDataIconKey } from '@/types/enums/sku-component-types'
import { SKU_VIDEO_ICONS } from './Icons'

type SkuVideoDataItem = SkuComponentConfigMap['skuVideoData']['items'][number]

function resolveIcon(key: SkuVideoDataIconKey | undefined) {
    if (!key) return null
    return SKU_VIDEO_ICONS[key] ?? null
}

function featureGridCols(itemCount: number) {
    if (itemCount <= 1) return 1
    if (itemCount > 4) return { base: 1, sm: 2, lg: 3 }
    return { base: 1, sm: 2, lg: itemCount }
}

export function FeatureGrid({ items }: { items: SkuVideoDataItem[] }) {
    const gridCols = useMemo(() => featureGridCols(items.length), [items.length])

    return (
        <MotionBox
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px 0px' }}
        >
            <SimpleGrid
                cols={gridCols}
                spacing={{ base: 24, sm: 32, md: 40 }}
                w="100%"
                mt={{ base: 28, sm: 36, md: 48 }}
            >
                {items.map((item, index) => {
                    const IconComponent = resolveIcon(item.icon)
                    return (
                        <MotionBox key={`${item.title}-${index}`} variants={staggerItem}>
                            <Box miw={0} pt={24} style={{ borderTop: '1px solid rgba(31, 30, 29, 0.12)' }}>
                                {IconComponent ? (
                                    <Box mb={24}>
                                        <IconComponent size={60} color="#2D1AFF" />
                                    </Box>
                                ) : null}
                                <Text c="gray.9" fz={24} fw={400} lh="100%" lts={-0.24} mb={item.description ? 12 : 0}>
                                    {item.title}
                                </Text>
                                {item.description ? (
                                    <Text c="gray.7" fz={14} fw={450} lh="150%" component="p" m={0}>
                                        {item.description}
                                    </Text>
                                ) : null}
                            </Box>
                        </MotionBox>
                    )
                })}
            </SimpleGrid>
        </MotionBox>
    )
}
